import { z } from 'zod';
import { ModuleManifest, ModuleResultPublic } from '@momentum/shared';
import { POC_CHOICE_ID } from '../poc-choice/manifest.ts';

/**
 * DISPOSABLE plain board. Proves: shared positions, a trivial adjacent-move rule, a marked node that launches a
 * child activity with selected participants, state preservation while the child runs, and exactly-once result
 * application to a visible test counter. No dice, turns, inventory or economy.
 */
export const POC_BOARD_ID = 'poc-board';

export const pocBoardManifest = ModuleManifest.parse({
  id: POC_BOARD_ID,
  version: '0.1.0',
  name: 'Board test (disposable)',
  description: 'Numbered nodes, placeholder tokens, adjacent moves, activity node launches the choice test.',
  disposable: true,
  players: { min: 2, max: 6 },
  participation: 'cooperative',
  teams: null,
  voice: { scope: 'shared' },
  capabilities: { launchesChildren: true, childModuleIds: [POC_CHOICE_ID], usesRandom: false, hasPrivateData: false, renderer: 'board' },
  launchableFromLobby: true,
});

export const PocBoardConfig = z.object({
  /** Who joins the child when someone lands on the activity node. */
  childParticipants: z.enum(['all', 'mover']).default('all'),
  /** Complete the board automatically after this many child results (null = host finishes manually). */
  endAfterLaunches: z.number().int().min(1).nullable().default(null),
  childConfig: z.unknown().optional(),
});
export type PocBoardConfig = z.infer<typeof PocBoardConfig>;

export const PocBoardAction = z.discriminatedUnion('type', [
  /** Move the actor's own token to an adjacent node. Any assigned player, any time, unless a child is active. */
  z.object({ type: z.literal('move'), to: z.string() }),
  /** Host only: complete the board and return the root result. */
  z.object({ type: z.literal('finish') }),
]);
export type PocBoardAction = z.infer<typeof PocBoardAction>;

/** Result payload, schema version 1. Board-owned progress data as a test counter. */
export const PocBoardResultPayload = z.object({
  schema: z.literal(1),
  counter: z.number().int(),
  launches: z.number().int(),
  moves: z.number().int(),
  finalPositions: z.record(z.string(), z.string()),
  childResults: z.array(ModuleResultPublic),
});
export type PocBoardResultPayload = z.infer<typeof PocBoardResultPayload>;
